/**
 * BdcPresenceRail — right-side sticky peer-presence widget on the BDC
 * portal. Polls ``/api/bdc/team/presence`` every 30s (same cadence as the
 * existing agent heartbeat) so status dots reflect reality within one
 * heartbeat cycle.
 *
 * Row click → creates-or-fetches a DM channel via POST /api/bdc/team/dm,
 * then routes to /bdc/team?channel=<id>. Fires-once — repeated clicks on
 * the same agent reuse the existing DM (backend is idempotent).
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Users } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const STATUS_DOT = {
  available: "bg-emerald-500",
  in_call:   "bg-amber-500 animate-pulse",
  away:      "bg-slate-400",
  offline:   "bg-slate-300",
};

const STATUS_ORDER = { available: 0, in_call: 1, away: 2, offline: 3 };

const initialsColor = (initials) => {
  // Deterministic bg color per pair of letters — same person → same avatar tint.
  const palette = ["bg-indigo-500", "bg-emerald-500", "bg-amber-500", "bg-rose-500", "bg-sky-500", "bg-fuchsia-500"];
  const hash = (initials || "").split("").reduce((s, c) => s + c.charCodeAt(0), 0);
  return palette[hash % palette.length];
};

const BdcPresenceRail = ({ compact = false, onOpenDm }) => {
  const nav = useNavigate();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/bdc/team/presence");
      // Sort: available > in_call > away > offline, then by name
      data.sort((a, b) => {
        const sa = STATUS_ORDER[a.status] ?? 9;
        const sb = STATUS_ORDER[b.status] ?? 9;
        if (sa !== sb) return sa - sb;
        return (a.full_name || "").localeCompare(b.full_name || "");
      });
      setAgents(data);
    } catch (e) {
      // Silent for polling — surface only on first mount error.
      if (loading) toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [loading]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [load]);

  const openDm = async (agent) => {
    if (opening) return;
    setOpening(agent.id);
    try {
      const { data } = await api.post("/bdc/team/dm", { other_bdc_id: agent.id });
      if (onOpenDm) onOpenDm(data.channel_id);
      else nav(`/bdc/team?channel=${data.channel_id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setOpening(null);
    }
  };

  return (
    <div
      className="rounded-2xl border border-slate-200 bg-white overflow-hidden"
      data-testid="bdc-presence-rail"
    >
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
        <Users size={14} className="text-slate-500" />
        <p className="text-[11px] uppercase tracking-widest font-semibold text-slate-600">Team presence</p>
        {loading && <Loader2 size={12} className="animate-spin text-slate-400 ml-auto" />}
      </div>
      <ul className="divide-y divide-slate-100">
        {agents.map((a) => (
          <li key={a.id}>
            <button
              type="button"
              onClick={() => openDm(a)}
              disabled={opening === a.id}
              className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 transition-colors text-left disabled:opacity-60"
              data-testid={`bdc-presence-agent-${a.id}`}
            >
              <span className={`relative w-8 h-8 rounded-full ${initialsColor(a.initials)} text-white text-xs font-semibold flex items-center justify-center shrink-0`}>
                {a.initials}
                <span className={`absolute -bottom-0 -right-0 w-2.5 h-2.5 rounded-full ring-2 ring-white ${STATUS_DOT[a.status] || STATUS_DOT.offline}`} />
              </span>
              <span className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-slate-900 truncate">{a.full_name}</p>
                {!compact && (
                  <p className={`text-[11px] truncate ${
                    a.status === "available" ? "text-emerald-700"
                    : a.status === "in_call" ? "text-amber-700"
                    : "text-slate-500"
                  }`}>
                    {a.status_label}
                  </p>
                )}
              </span>
              {opening === a.id && <Loader2 size={12} className="animate-spin text-slate-400" />}
            </button>
          </li>
        ))}
        {!agents.length && !loading && (
          <li className="px-4 py-4 text-xs text-slate-400 italic">No other BDC agents.</li>
        )}
      </ul>
    </div>
  );
};

export default BdcPresenceRail;
